import React, { useState, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Activity,
  Database,
  HardDrive,
  FileText,
  Trash2,
  AlertTriangle,
  CheckCircle2,
  RefreshCw,
  Loader2,
  Zap,
  ShieldCheck,
} from 'lucide-react';
import api from '../lib/api';

export function SystemHealthPanel() {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(false);
  const [purging, setPurging] = useState(false);
  const [purgeTarget, setPurgeTarget] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [ensuringIndexes, setEnsuringIndexes] = useState(false);

  const loadHealth = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/system-health/status');
      setHealth(data);
    } catch (err) {
      toast.error('Failed to load system health');
    } finally {
      setLoading(false);
    }
  }, []);

  const handlePurge = async () => {
    if (!purgeTarget) return;
    setPurging(true);
    setConfirmOpen(false);
    try {
      const { data } = await api.post('/system-health/purge', {
        target: purgeTarget.key,
        days_to_keep: purgeTarget.key === 'old_audit' ? 90 : undefined,
      });
      toast.success(data.message);
      loadHealth();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Purge failed');
    } finally {
      setPurging(false);
      setPurgeTarget(null);
    }
  };

  const handleEnsureIndexes = async () => {
    setEnsuringIndexes(true);
    try {
      const { data } = await api.post('/system-health/ensure-indexes');
      toast.success(data.message);
      loadHealth();
    } catch (err) {
      toast.error('Failed to ensure indexes');
    } finally {
      setEnsuringIndexes(false);
    }
  };

  const openPurgeConfirm = (key, label) => {
    setPurgeTarget({ key, label });
    setConfirmOpen(true);
  };

  const levelColor = (level) => {
    if (level === 'danger') return 'destructive';
    if (level === 'warning') return 'secondary';
    return 'outline';
  };

  const levelIcon = (level) => {
    if (level === 'danger') return <AlertTriangle className="w-3.5 h-3.5 text-red-500" />;
    if (level === 'warning') return <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />;
    return <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />;
  };

  return (
    <Card className="border-cyan-200 bg-cyan-50/30" data-testid="system-health-panel">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-cyan-700 flex items-center gap-2">
            <Activity className="w-5 h-5" /> System Health Monitor
          </CardTitle>
          <Button
            variant="outline"
            size="sm"
            onClick={loadHealth}
            disabled={loading}
            data-testid="btn-scan-health"
          >
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            {health ? 'Rescan' : 'Run Health Scan'}
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {!health && !loading && (
          <div className="text-center py-8 text-muted-foreground">
            <Activity className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p className="text-sm">Click "Run Health Scan" to check system health</p>
          </div>
        )}

        {loading && !health && (
          <div className="text-center py-8">
            <Loader2 className="w-8 h-8 mx-auto mb-3 animate-spin text-cyan-600" />
            <p className="text-sm text-muted-foreground">Scanning system...</p>
          </div>
        )}

        {health && (
          <>
            {/* Disk Usage */}
            <div className="p-3 rounded-lg bg-background border">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <HardDrive className="w-4 h-4" /> Disk Usage
                </div>
                <span className="text-xs font-mono text-muted-foreground">
                  {health.disk.used_gb} / {health.disk.total_gb} GB
                </span>
              </div>
              <Progress
                value={health.disk.used_percent}
                className="h-2"
              />
              <div className="flex justify-between mt-1">
                <span className="text-[10px] text-muted-foreground">{health.disk.used_percent}% used</span>
                <span className="text-[10px] text-muted-foreground">{health.disk.free_gb} GB free</span>
              </div>
            </div>

            {/* Database Summary */}
            <div className="p-3 rounded-lg bg-background border">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <Database className="w-4 h-4" /> Database
                </div>
                <span className="text-xs font-mono">
                  {health.database.total_size_kb.toFixed(0)} KB / {health.database.total_docs} docs
                </span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
                {Object.entries(health.database.collections)
                  .sort((a, b) => b[1].size_kb - a[1].size_kb)
                  .slice(0, 8)
                  .map(([name, info]) => (
                    <div key={name} className="text-xs p-2 bg-muted/50 rounded">
                      <p className="font-mono font-medium truncate">{name}</p>
                      <p className="text-muted-foreground">{info.docs} docs / {info.size_kb} KB</p>
                    </div>
                  ))}
              </div>
            </div>

            {/* Audit Logs, Rotation Policy */}
            {health.risks.length > 0 && (
              <div className="p-3 rounded-lg bg-background border">
                <p className="text-sm font-medium mb-2 flex items-center gap-2">
                  <Database className="w-4 h-4" /> Audit Logs
                </p>
                <div className="space-y-2">
                  {health.risks.map((r) => (
                    <div key={r.collection}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {levelIcon(r.level)}
                          <span className="text-sm font-mono">{r.docs} entries</span>
                          <Badge variant={levelColor(r.level)} className="text-[10px]">
                            {r.size_mb || (r.size_kb / 1024).toFixed(1)} MB / 1 GB
                          </Badge>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 text-xs"
                          onClick={() => openPurgeConfirm('rotate_audit', 'Audit Log Rotation')}
                          disabled={purging}
                          data-testid="btn-rotate-audit"
                        >
                          <RefreshCw className="w-3 h-3 mr-1" /> Rotate Now
                        </Button>
                      </div>
                      <p className="text-[10px] text-muted-foreground mt-1">
                        {r.policy || health.rotation_policy?.audit_logs?.description || 'Auto-rotates at 5 months or 1 GB'}
                        {r.oldest_age_days != null && ` | Oldest entry: ${r.oldest_age_days} days`}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Logs */}
            <div className="p-3 rounded-lg bg-background border">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <FileText className="w-4 h-4" /> Log Files
                  <Badge variant="outline" className="text-[10px]">{health.logs.total_kb.toFixed(0)} KB</Badge>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                  onClick={() => openPurgeConfirm('logs', 'Log Files')}
                  disabled={purging}
                  data-testid="btn-purge-logs"
                >
                  <Trash2 className="w-3 h-3 mr-1" /> Truncate All
                </Button>
              </div>
              <div className="space-y-1">
                {health.logs.files.map((f) => (
                  <div key={f.path} className="flex justify-between text-xs">
                    <span className="font-mono truncate text-muted-foreground">{f.path.split('/').pop()}</span>
                    <span className="font-mono whitespace-nowrap">
                      {f.size_kb.toFixed(0)} KB / {f.lines} lines
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Temp Files */}
            <div className="p-3 rounded-lg bg-background border">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <Trash2 className="w-4 h-4" /> Temp Files
                  <Badge variant="outline" className="text-[10px]">{health.temp_files.count} files / {health.temp_files.total_kb.toFixed(0)} KB</Badge>
                </div>
                {health.temp_files.count > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                    onClick={() => openPurgeConfirm('temp_files', 'Temp Files')}
                    disabled={purging}
                    data-testid="btn-purge-temp"
                  >
                    <Trash2 className="w-3 h-3 mr-1" /> Clean Up
                  </Button>
                )}
              </div>
              {health.temp_files.count === 0 && (
                <p className="text-xs text-emerald-600 flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" /> No temp files found
                </p>
              )}
            </div>

            {/* Index Health */}
            <div className="p-3 rounded-lg bg-background border">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <Zap className="w-4 h-4" /> Index Health
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={handleEnsureIndexes}
                  disabled={ensuringIndexes}
                  data-testid="btn-ensure-indexes"
                >
                  {ensuringIndexes ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <ShieldCheck className="w-3 h-3 mr-1" />}
                  Ensure Indexes
                </Button>
              </div>
              {health.missing_indexes.length === 0 ? (
                <p className="text-xs text-emerald-600 flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" /> All recommended indexes present
                </p>
              ) : (
                <div className="space-y-1">
                  {health.missing_indexes.map((idx, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs">
                      <AlertTriangle className="w-3 h-3 text-amber-500" />
                      <span className="font-mono">{idx.collection}.{idx.field}</span>
                      <span className="text-muted-foreground">, missing index</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </CardContent>

      {/* Purge Confirmation Dialog */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5" /> Confirm Purge
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to {purgeTarget?.key === 'rotate_audit' ? 'run' : 'purge'} <strong>{purgeTarget?.label}</strong>?
              {purgeTarget?.key === 'rotate_audit' && ' This will remove audit entries older than 5 months and trim if over 1 GB.'}
              {purgeTarget?.key === 'logs' && ' This will truncate all supervisor log files to 0 bytes.'}
              {purgeTarget?.key === 'temp_files' && ' This will remove all .pdf, .csv, .tmp, .log files from temp directories.'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={handlePurge}
              disabled={purging}
              data-testid="btn-confirm-purge"
            >
              {purging ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Purge
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
